/* ============================================================
   EGOSS ATELIER — Smart sticky header + back-to-top FAB
   Site-wide scroll-driven behaviours.

   1. Smart sticky header
      - atelier-header is forced position: sticky via CSS override
        in egoss-overrides.css.liquid (regardless of the data-sticky
        theme-editor toggle).
      - This JS toggles .atelier-header--hidden when scroll direction
        changes, producing a hide-on-scroll-down / reveal-on-scroll-up
        behaviour.
      - Pauses itself when any dialog / drawer / modal is open so the
        header stays where the overlay expects it.

   2. Back-to-top FAB
      - <atelier-back-to-top> custom element registers here (moved
        from egoss-collection-loadmore.js so it works on every page,
        not just collection pages).
      - Shows when scrollY > 600, hides otherwise. Smooth-scrolls
        to top on click, instant scroll if prefers-reduced-motion.
   ============================================================ */

(function () {
  'use strict';

  /* ---------- 1. Smart sticky header ---------- */

  var HEADER_HIDE_THRESHOLD = 100;
  var HEADER_HIDDEN_CLASS = 'atelier-header--hidden';

  var isScrollLocked = function () {
    // Prestige locks scroll when any dialog / drawer / modal is open.
    // Multiple heuristics because different overlay types use different
    // signals: sidebar + search + cart drawer + x-modal + native dialog.
    if (
      document.documentElement.classList.contains('locked') ||
      document.body.classList.contains('locked') ||
      document.documentElement.classList.contains('overflow-hidden') ||
      document.body.classList.contains('overflow-hidden')
    ) {
      return true;
    }
    var openModalSelectors = [
      'header-sidebar[open]',
      'header-search[open]',
      'cart-drawer[open]',
      'quick-buy-modal[open]',
      'x-modal[open]',
      'dialog[open][aria-modal="true"]'
    ].join(',');
    return document.querySelector(openModalSelectors) !== null;
  };

  // Phase 5-ux-fix (2026-04-24): two defensive tweaks fixing reveal-on-up
  // failure on mobile collection pages.
  //
  //   5A — Minimum-delta direction detection (REVEAL_DELTA / HIDE_DELTA = 5px).
  //        iOS Safari rAF batches scroll events during momentum and touch.
  //        A bare `y < lastY` check would flip on a 1px overscroll bounce or
  //        on micro-jitter. Requiring 5px in either direction filters those.
  //
  //   5B — Document-height-change resync. AtelierLoadMore on collection pages
  //        does silent fetch + insertBefore prepends during state restore.
  //        Browser scroll-anchoring then bumps scrollY artificially to keep
  //        the visible content stable, which the bare handler reads as a
  //        scroll-down event. We detect docHeight changes between samples and
  //        skip the direction logic for that tick, just resync lastY.
  var REVEAL_DELTA = 5;
  var HIDE_DELTA = 5;

  var initSmartHeader = function () {
    var header = document.querySelector('atelier-header');
    if (!header) return;

    var lastY = window.scrollY;
    var lastDocHeight = document.documentElement.scrollHeight;
    var ticking = false;

    var update = function () {
      var y = window.scrollY;
      var docHeight = document.documentElement.scrollHeight;
      ticking = false;

      if (isScrollLocked()) {
        // Don't hide the header while an overlay controls the page.
        lastY = y;
        lastDocHeight = docHeight;
        return;
      }

      // 5B: page height changed (loadmore prepend / silent fetch) → the
      // observed scrollY shift is a scroll-anchor adjustment, not a user
      // scroll. Resync baseline without firing direction logic.
      if (docHeight !== lastDocHeight) {
        lastDocHeight = docHeight;
        lastY = y;
        return;
      }

      if (y <= 0) {
        header.classList.remove(HEADER_HIDDEN_CLASS);
      } else if (y - lastY >= HIDE_DELTA && y > HEADER_HIDE_THRESHOLD) {
        // 5A: scrolled down ≥5px past threshold → hide
        header.classList.add(HEADER_HIDDEN_CLASS);
      } else if (lastY - y >= REVEAL_DELTA) {
        // 5A: scrolled up ≥5px → reveal
        header.classList.remove(HEADER_HIDDEN_CLASS);
      }

      lastY = y;
    };

    window.addEventListener(
      'scroll',
      function () {
        if (!ticking) {
          window.requestAnimationFrame(update);
          ticking = true;
        }
      },
      { passive: true }
    );

    // When a desktop mega-menu is hovered open, keep the header visible
    // so the menu stays reachable — guard against a concurrent scroll-down
    // event hiding it mid-interaction.
    document.addEventListener('mouseenter', function (e) {
      if (!e.target || !e.target.matches) return;
      if (e.target.matches('atelier-header details[open], atelier-header .header__menu-disclosure[open]')) {
        header.classList.remove(HEADER_HIDDEN_CLASS);
      }
    }, true);
  };

  /* ---------- 2. Back-to-top custom element ---------- */

  var AtelierBackToTop = class extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button) return;

      this.threshold = 600;
      this.lastVisible = false;
      this.boundOnScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.boundOnScroll, { passive: true });
      this.button.addEventListener('click', this.scrollTop.bind(this));
      this.onScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.boundOnScroll);
    }

    onScroll() {
      var visible = window.scrollY > this.threshold;
      if (visible !== this.lastVisible) {
        this.classList.toggle('is-visible', visible);
        this.setAttribute('aria-hidden', visible ? 'false' : 'true');
        this.lastVisible = visible;
      }
    }

    scrollTop(event) {
      if (event) event.preventDefault();
      var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
    }
  };

  if (!window.customElements.get('atelier-back-to-top')) {
    window.customElements.define('atelier-back-to-top', AtelierBackToTop);
  }

  /* ---------- boot ---------- */

  var boot = function () {
    initSmartHeader();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
